import { ApiError } from "@aurumiq/api-client";
import { useQuery } from "@tanstack/react-query";
import { rawApi } from "@/lib/api";
import { useAuth } from "./auth-context";

export type StaffRole = "superadmin" | "support" | "sales" | "content_editor" | "pending";

export interface Me {
  id?: string;
  email: string;
  name: string;
  role: StaffRole;
  /** e.g. ["*"], ["orgs:read", "licenses:*"] */
  permissions: string[];
  is_active: boolean;
  /** true when `/auth/me` is not implemented yet and we fell back to a stub. */
  stub?: boolean;
}

export function useMe() {
  const auth = useAuth();
  return useQuery({
    queryKey: ["auth", "me", auth.mode, auth.user?.email],
    enabled: auth.ready && auth.isAuthenticated,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<Me> => {
      try {
        return await rawApi.get<Me>("/api/v1/auth/me");
      } catch (err) {
        // Degrade to a client-side stub when the endpoint is missing/unreachable, or when the
        // fake test token is sent to an API that is not running in test mode (e2e against dev).
        const authFailure = err instanceof ApiError && (err.isUnauthorized || err.isForbidden);
        if (
          err instanceof ApiError &&
          (err.isNotFound || err.isNetwork || (auth.mode === "test" && authFailure))
        ) {
          const role = (
            auth.mode === "test" ? (await auth.getToken())?.split(":")[1] : "superadmin"
          ) as StaffRole;
          return {
            email: auth.user?.email ?? "",
            name: auth.user?.name ?? "",
            role: role ?? "superadmin",
            permissions: role === "pending" ? [] : ["*"],
            is_active: true,
            stub: true,
          };
        }
        throw err;
      }
    },
  });
}

export function hasPermission(perms: string[] | undefined, perm: string): boolean {
  if (!perms) return false;
  if (perms.includes("*")) return true;
  if (perms.includes(perm)) return true;
  const domain = perm.split(":")[0];
  return perms.includes(`${domain}:*`);
}

/** Hides what the server enforces. Returns `false` while `/auth/me` is loading. */
export function usePermission(perm: string): boolean {
  const me = useMe();
  return hasPermission(me.data?.permissions, perm);
}
